import { sectionForType } from './line-sections';
import { costContribution, isLineSold, revenueContribution } from './line-economics';
import type { PricingTreatment } from './line-economics';
import { isTMOverrideEligibleType, isMaterializedDynamicService } from './pricingMode';
import { groupComponentLines, soldLineSet } from './service-components';
import { serviceCost } from './service-cost';
import { marginShareLaborTotal, marginSharePercentOf } from './service-labor';
import type { DynamicServicePricingConfig, ServiceLaborLine } from '@/types/pricebook';

/**
 * What a document costs us, against what it sells for.
 *
 * Cost is read off the lines rather than stored: every line already carries the
 * unit cost it was priced from, so the roll-up cannot drift away from the
 * pricing the operator actually sees.
 *
 * PRICE IS NOT COMPUTED HERE, and that separation is the point. A service is
 * priced one of two ways — FLAT, where the operator types the number, or
 * DYNAMIC, where the formula derives it (see SPEC-line-item-model, "Flat vs
 * Dynamic"). This module is blind to which: a service's hours cost what they
 * cost either way, and whether those same hours also EARN anything is a
 * question for the pricing engine alone.
 */

export interface CostableLine {
  type: string;
  quantity: number;
  basePrice: number;
  costBasis?: number;
  /**
   * Hourly BILLING rate — what an hour SELLS for on a dynamic service.
   *
   * Read by the pricing engine, never by this module. The invoice write path
   * back-fills it from the unit price when it is absent, so on a flat line it
   * is frequently just the price wearing a different name; costing from it
   * would report cost = price and pin every hourly service at 0% margin, which
   * is exactly the bug this separation fixes.
   */
  laborRate?: number;
  /**
   * The hourly rate the recorded time is worth, REDUCED from this line's labor
   * rows (total worth over total hours) so costing has one number to multiply.
   *
   * There is no separate "cost rate" concept: a labor rate is what labor per
   * unit of time is worth, and it is the same figure whichever way the service
   * is priced. What differs is what it DOES — a dynamic service is priced from
   * it, a flat service already has its price so the same money lands here, in
   * cost and gross margin.
   *
   * Distinct from `laborRate` only because that field is not trustworthy on a
   * document line: the invoice write path back-fills it from the unit price, so
   * costing from it would report cost = price. This one is derived from the
   * rows the operator actually entered.
   */
  laborCostRate?: number;
  priceStrategy?: string;
  /**
   * Hours quoted against this service — on a FLAT service as well.
   *
   * PER UNIT. One delivery of the service takes this long; a line selling two of
   * it takes twice as long and costs twice as much (`serviceLabor`).
   *
   * On a flat service they are COST ONLY: the price stays the number the
   * operator typed, and the hours move gross margin the way a material's cost
   * does. On a dynamic service the same hours also drive price, through the
   * formula, which is resolved elsewhere.
   */
  estimatedHours?: number;
  /**
   * A materialized DYNAMIC service's frozen pricing, whose `laborLines` hold
   * its time and what that time is worth.
   *
   * Such a service stores `laborRate` and `costBasis` as 0 — it has no single
   * hourly rate — so costing it from those reported $0 of labor against real
   * hours, and then warned that the hours had no rate behind them. Both were
   * artefacts of asking the wrong field.
   */
  dynamicPricingConfig?: DynamicServicePricingConfig | null;
  /**
   * The service's labor rows as the document carries them.
   *
   * Read for ONE thing: a row paid as a share of the margin, whose frozen
   * percentage is the only place that share is recorded. The HOURLY rows are
   * deliberately not costed from here — they reduce to `estimatedHours` and
   * `laborCostRate`, which is what this module has always multiplied, and a
   * tiered row cannot be resolved on a document anyway (no profiles to look it
   * up in). See `serviceLabor`.
   */
  laborCostLines?: ServiceLaborLine[] | null;
  /** Optional lines the customer has not taken are offered, not sold. */
  isSelected?: boolean;
  isOptional?: boolean;
  /**
   * This line's own key, and the key of the service carrying it.
   *
   * A service's parts are their own document lines pointing back at it, which
   * is the only way costing can tell that a $681 water heater belongs to an
   * install nobody bought. `groupComponentLines` reads the link — costing does
   * not interpret it itself — so what counts as a part here is exactly what
   * counts as one everywhere else. Callers have always passed both; only the
   * declaration was missing.
   */
  lineKey?: string | null;
  parentLineKey?: string | null;
  /**
   * Whether the line is billed at its own price, and how.
   *
   * A LINE'S COST IS BLIND TO BOTH: a part we do not charge for separately
   * still costs what it costs, and dropping it would report a margin the job
   * does not earn (`lineCost`, and the invariant test that pins it).
   *
   * What they answer is the different question of WHOSE sale the part is in.
   * A part billed on its own line is sold on its own terms — the engine prices
   * it standalone even when the service that listed it was declined — while a
   * part recovered through its service has no money of its own anywhere, so
   * when nobody buys the service, nobody buys the part. See `costDocument`.
   */
  chargeable?: boolean;
  pricingTreatment?: PricingTreatment | null;
}

export interface DocumentCosting {
  labor: number;
  /**
   * Of that labor, the part paid as a share of the margin rather than earned by
   * the hour. Carried separately so a panel can name it, and so
   * `labor - laborFromMargin` remains the hourly figure.
   */
  laborFromMargin: number;
  /**
   * The percentage `laborFromMargin` was taken at, so a panel can print
   * "Labor (18% share)". `null` when no sold service pays a share, AND when
   * two services pay different ones: one figure would misstate both.
   */
  laborSharePercent: number | null;
  /** Quoted hours behind the labor figure — flat and dynamic services alike. */
  laborHours: number;
  material: number;
  equipment: number;
  totalCost: number;
  /**
   * Hours recorded with no cost rate behind them.
   *
   * Surfaced rather than swallowed: those hours cost the job something and are
   * being counted at zero, so margin reads high for a reason the operator
   * should be told about instead of trusting a number that flatters the job.
   */
  hoursMissingCostRate: number;
}

export interface DocumentMargin extends DocumentCosting {
  priceTotal: number;
  /**
   * What the document earns over its materials and equipment — before labor.
   *
   * The figure an operator needs when labor is paid out of the margin rather
   * than by the hour: it is the money there is to pay labor FROM. With no
   * margin-share labor anywhere on the document it is simply the net plus the
   * labor cost, and saying so still costs nothing to read.
   */
  contributionAmount: number;
  contributionPercent: number | null;
  /**
   * What the business keeps once labor is paid — the bottom line.
   *
   * Named `net` rather than `gross` because it now has something to be net OF.
   * The fields it replaces (`grossMarginPercent` / `netAmount`) measured this
   * same distance, and were renamed rather than kept beside the new pair so
   * nobody has to guess which of two "margins" a screen is printing.
   */
  netMarginPercent: number | null;
  netMarginAmount: number;
}

const num = (value: number | null | undefined): number =>
  Number.isFinite(value) ? (value as number) : 0;

const round2 = (value: number): number => Math.round(value * 100) / 100;

/**
 * Is this line part of the sale?
 *
 * ONE predicate, shared with the pricing preview — `isLineSold` in
 * `line-economics` states it and both sides read it. Costing used to ask
 * `isSelected !== false` by itself, which is a different question: a line
 * unticked without being marked optional earned revenue on one side and cost
 * nothing on the other, so margin read high by exactly that line. Cost and
 * revenue must answer selection identically, or the margin between them is not
 * a measurement of anything.
 */
const isSold = isLineSold;

/**
 * Hours of labor recorded against a line.
 *
 * SERVICES ONLY, and every service — flat or dynamic. What the two modes
 * disagree about is PRICE, not time: a flat service prices the outcome, so its
 * hours never reach the price, but the job still takes those hours and they
 * still cost money. Reading zero here for a flat service was the bug — it left
 * an operator who had sold a fixed price with no way to record what delivering
 * it costs, and reported the whole price as margin.
 *
 * Bundles, memberships, fees and one-off lines all fall to the services SECTION
 * but never quote hours; gating on the type keeps a stray field on one of them
 * from inventing labor.
 *
 * A materialized dynamic service answers from its CONFIGURATION rather than from
 * the stored `estimatedHours`, because that is where its time lives and where the
 * operator edits it — see `serviceLabor`.
 *
 * The LINE's hours, not one unit's: whichever source states the time, it states
 * it PER UNIT, and selling two of a service takes twice as long.
 */
export function lineLaborHours(line: CostableLine): number {
  return serviceLabor(line).hours;
}

/**
 * The hours STORED on the line, which is what a service without a dynamic
 * configuration is costed from.
 *
 * Separate from `lineLaborHours` because that answer now comes back out of
 * `serviceCost`, which this feeds: a materialized dynamic service keeps its time
 * in its configuration and the stored figure is only the starting point.
 */
function quotedHours(line: CostableLine): number {
  if (!isTMOverrideEligibleType(line.type)) return 0;
  return Math.max(0, num(line.estimatedHours));
}

/**
 * What a service line's TIME is, and what it costs — the one reading of both.
 *
 * They come back together because they are one answer. A materialized dynamic
 * service is costed from its CONFIGURATION, and the configuration is editable on
 * the document: changing the labor duration in the wizard moved the cost while
 * `estimatedHours` — written once at materialization and never again — kept
 * stating the catalog's time, so the margin panel read "Labor (1 hr) $3,000.00"
 * for three configured hours. Taking both figures from `serviceCost` means the
 * hours shown are the hours charged.
 *
 * `serviceCost` is deliberately unable to read `costBasis` as the service's
 * cost — that column is 0 for a dynamically-priced service and three surfaces
 * once reported such a service as free. Its parts are their own document lines
 * and cost there, so none are passed here and nothing is counted twice.
 */
function serviceLabor(
  line: CostableLine,
  contribution = 0,
): { cost: number; hours: number; fromMargin: number; sharePercent: number } {
  const result = serviceCost(
    {
      dynamicPricingConfig: isMaterializedDynamicService(line) ? line.dynamicPricingConfig : null,
      estimatedHours: quotedHours(line),
      hourlyCost: laborCostRateOf(line),
    },
    [],
    // A document line's snapshot carries its own resolved rates, so no profile
    // lookup is needed or possible here.
    [],
    // No revenue: the margin share is added below, against the whole card's
    // contribution, which this one line cannot see.
    null,
  );
  // `serviceCost` answers for ONE of the service — its configuration and its
  // `estimatedHours` are both per unit. The LINE is however many of it are being
  // sold, so both figures are multiplied out here, the same way `costContribution`
  // multiplies a part's unit cost below.
  //
  // Leaving them per-unit was the reported bug (NOT-630): a 2.5-hour service sold
  // twice reported 2.5 hours and one unit's labor cost, against a price that had
  // doubled — so the margin panel showed margin the job does not earn, and the
  // hours the schedule was planned from were half the real time.
  const units = Math.max(0, num(line.quantity));

  /**
   * The margin share, added on top of the hourly answer above.
   *
   * It is computed here rather than inside `serviceCost` because on a DOCUMENT
   * a service's parts are their own lines: the money this share is taken from
   * is a property of the whole card, which only `costDocument` can see. It
   * therefore arrives as `contribution`, already multiplied out to the line.
   *
   * NO PROFILES ARE PASSED, and that is the point of freezing the percentage at
   * materialization: a document states the share it was sold at, and a profile
   * edited afterwards does not restate what a signed estimate earned.
   */
  const fromMargin = marginShareLaborTotal(line.laborCostLines ?? [], [], contribution);

  return {
    cost: round2(result.labor * units + fromMargin),
    hours: result.laborHours * units,
    fromMargin: round2(fromMargin),
    sharePercent: marginSharePercentOf(line.laborCostLines ?? [], []),
  };
}

/**
 * What one line costs us.
 *
 * A service costs its TIME: hours x hourly cost x quantity. Materials and
 * equipment cost their unit cost per unit sold. A service's parts are their own
 * lines and cost there, so nothing is counted twice.
 *
 * Both cases are the SAME multiplication — cost x quantity, RULE TWO, which
 * `line-economics` owns — differing only in what a unit is: for a part the unit
 * is the part, for a service it is one delivery of the service, whose cost is
 * the time it takes. Chargeability and visibility do not appear because the rule
 * is blind to them, and asking the canonical function is what keeps it that way.
 */
export function lineCost(line: CostableLine): number {
  if (sectionForType(line.type) === 'services') {
    // A DYNAMIC service states its time as LABOR LINES, each with its own rate,
    // so there is no single hourly figure to multiply; `serviceLabor` prefers the
    // configuration for exactly that reason. Everything else falls to hours x the
    // hourly cost, which is the right question for a service typed straight onto
    // a document.
    return serviceLabor(line).cost;
  }
  return costContribution(line);
}

/**
 * The hourly rate this line's recorded time is worth.
 *
 * The rate reduced from the line's labor rows first. `costBasis` is the
 * fallback for a service typed straight onto a document without rows behind it
 * — the line's own unit cost, which for a service measured in hours is hourly.
 *
 * `laborRate` is NOT consulted, and that is about TRUST rather than about it
 * being a different kind of number: the invoice write path back-fills it from
 * the unit price, so reading it would report cost = price on every flat line.
 */
export function laborCostRateOf(line: CostableLine): number {
  const rate = num(line.laborCostRate);
  return rate > 0 ? rate : num(line.costBasis);
}

/**
 * What a service card earns over its parts: the service line's revenue plus
 * each part's revenue less its cost. See `costDocument` for why the card, and
 * not the line or the document, is what a margin share is taken from.
 */
function contributionOfCard(service: CostableLine, parts: CostableLine[]): number {
  return round2(
    parts.reduce(
      (total, part) => total + revenueContribution(part) - costContribution(part),
      revenueContribution(service),
    ),
  );
}

/**
 * What each line costs, for the ROW it is printed on.
 *
 * `lineCost` answers that for every line but one kind: a service paying labor
 * as a SHARE OF THE MARGIN, whose cost depends on what its whole card earns —
 * which a single line cannot see. Asked alone, that service costs nothing and
 * its row reports a 100% margin the job total beside it does not.
 *
 * So the card is resolved here by the same grouping and the same contribution
 * `costDocument` uses. A row keeps the per-line rule it always had: it costs
 * what it would cost if sold, so a declined service still shows its cost, and
 * its share is taken from the parts that are themselves selected.
 */
export function documentRowCosts(lines: CostableLine[]): Map<CostableLine, number> {
  const costs = new Map<CostableLine, number>();
  for (const group of groupComponentLines(lines)) {
    for (const part of group.components) costs.set(part, lineCost(part));
    const parent = group.parent;
    if (sectionForType(parent.type) !== 'services') {
      costs.set(parent, lineCost(parent));
      continue;
    }
    const contribution = contributionOfCard(parent, group.components.filter(isSold));
    costs.set(parent, serviceLabor(parent, contribution).cost);
  }
  return costs;
}

/**
 * Cost split across the three sections the editor groups lines into.
 *
 * `_pricingMode` is accepted so callers need not change, and ignored: it says
 * whether this document PRICES its services dynamically, and cost does not
 * depend on that. Hours cost what they cost under either mode.
 */
export function costDocument(
  lines: CostableLine[],
  _pricingMode: 'STANDARD' | 'TM_OVERRIDE' = 'STANDARD',
): DocumentCosting {
  let labor = 0;
  let laborFromMargin = 0;
  const sharePercents = new Set<number>();
  let laborHours = 0;
  let material = 0;
  let equipment = 0;
  let hoursMissingCostRate = 0;

  const count = (line: CostableLine, contribution = 0): void => {
    switch (sectionForType(line.type)) {
      case 'materials':
        material += lineCost(line);
        break;
      case 'equipment':
        equipment += lineCost(line);
        break;
      default: {
        // One reading of the line's labor, so the hours printed beside the money
        // are the hours the money was charged for.
        const { cost, hours, fromMargin, sharePercent } = serviceLabor(line, contribution);
        laborHours += hours;
        labor += cost;
        laborFromMargin += fromMargin;
        if (sharePercent > 0) sharePercents.add(sharePercent);
        // Flagged on the OUTCOME, not on one field: hours that produced no cost
        // are the thing worth warning about, however the cost was arrived at.
        // Asking `laborCostRateOf` directly warned about every dynamic service,
        // whose rate lives in its labor lines rather than in that field.
        if (hours > 0 && cost <= 0) hoursMissingCostRate += hours;
      }
    }
  };

  /**
   * A SERVICE AND ITS PARTS, resolved by the one function that resolves them.
   *
   * `groupComponentLines` is the document's bundle model — the same resolver
   * the customer's document, the reorder maths and (in lockstep) the pricing
   * preview's `resolvePreviewBundles` use. Costing asks it rather than reading
   * `parentLineKey` itself, so a part is a part here exactly when it is one
   * everywhere else: only a SERVICE carries parts, a key claimed by two lines
   * anchors nothing, and an orphan whose service was deleted stands on its own.
   * Costing's own key map got two of those three wrong, and made the answer
   * depend on array order.
   *
   * Every line comes back in exactly one group, so nothing is dropped or
   * counted twice however the links are shaped — including a loop, which cannot
   * survive a model where a part carries no parts of its own.
   */
  // THE sale, resolved once by the same function revenue asks (`soldLineSet`,
  // which mirrors the engine's `soldLines`). The grouper above says which
  // lines belong to which CARD; it anchors only on top-level rows, so a chain
  // `service → kit → fitting` leaves the fitting as its own group and costing
  // charged for it after pricing had dropped it — the price-vs-margin drift
  // this module exists to prevent. Membership in the SALE is not the grouper's
  // question.
  const soldSet = soldLineSet(lines);
  const inTheSale = (line: (typeof lines)[number]) => soldSet.has(line);

  for (const group of groupComponentLines(lines)) {
    const serviceSold = isSold(group.parent) && inTheSale(group.parent);
    const soldParts = serviceSold
      ? group.components.filter((part) => isSold(part) && inTheSale(part))
      : [];

    /**
     * WHAT THIS CARD EARNS OVER ITS PARTS — the money a margin-share labor row
     * takes its percentage from, and nothing else reads it.
     *
     * Measured across the whole card because that is what a service IS on a
     * document: the service line plus the part rows pointing at it. An included
     * part contributes no revenue of its own (`revenueContribution` returns 0
     * for a line that charges nothing — its money is already inside the service
     * price) while still costing what it costs, so the card's contribution comes
     * out the same whether the operator bills the parts separately or inside the
     * service. Presentation must not move what the crew is paid.
     *
     * THE LINE'S OWN PRICE, not the document's post-discount total. A discount
     * granted on the document reduces what the business keeps without reducing
     * what the crew is owed — the share is a property of the service as priced,
     * and the discount is the document's business. It is also what keeps this
     * figure identical to the one the pricebook shows for the same service,
     * which is the whole point of the share cascading.
     */
    const cardContribution = serviceSold ? contributionOfCard(group.parent, soldParts) : 0;

    if (serviceSold) count(group.parent, cardContribution);

    for (const part of group.components) {
      if (!isSold(part) || !inTheSale(part)) continue;

      /**
       * A DECLINED SERVICE TAKES ITS PARTS DOWN WITH IT.
       *
       * Unticking Selected on an optional service leaves its parts each saying
       * `isSelected: true` — a part is its own document line and nothing ever
       * wrote to it. Counting them one at a time charged the job for an install
       * nobody bought: $681 of water heater against a $0.00 price, net −$681
       * (NOT-635). With the service declined the parts are not sold — not the
       * included ones, whose money was only ever inside the service's price,
       * and not the billed-extra ones either, which the pricing engine now
       * prices at nothing for exactly the same reason (`declineParts`). Cost
       * follows revenue out, or margin is a lie.
       *
       * DERIVED, never written down. Cascading the parent's flag onto its
       * children at untick time would destroy information: a part the operator
       * unticked on its own becomes indistinguishable from one the cascade
       * turned off, and re-ticking the service resurrects a part they
       * deliberately dropped.
       */
      if (!serviceSold) continue;

      count(part);
    }
  }

  return {
    labor,
    laborFromMargin: round2(laborFromMargin),
    laborSharePercent: sharePercents.size === 1 ? [...sharePercents][0]! : null,
    laborHours,
    material,
    equipment,
    totalCost: labor + material + equipment,
    hoursMissingCostRate,
  };
}

/**
 * The costing plus what it means against the sell price.
 *
 * `priceTotal` is passed in rather than summed here: the document's price comes
 * from the pricing engine, including discounts and adjustments this module
 * knows nothing about, and recomputing it would be a second answer to a
 * question already answered. It is also what keeps a flat service's hours
 * cost-only — the price arrives already settled, so nothing here can move it.
 */
export function marginFor(
  lines: CostableLine[],
  priceTotal: number,
  pricingMode: 'STANDARD' | 'TM_OVERRIDE' = 'STANDARD',
): DocumentMargin {
  const costing = costDocument(lines, pricingMode);
  const netMarginAmount = priceTotal - costing.totalCost;
  // Before labor: the parts are already spent, the crew is not yet paid.
  const contributionAmount = round2(priceTotal - costing.material - costing.equipment);

  return {
    ...costing,
    priceTotal,
    contributionAmount,
    // A document with no price has no margin — 0/0 is not 0%, and showing 0%
    // on an empty estimate reads as "this job makes nothing".
    contributionPercent: priceTotal > 0 ? (contributionAmount / priceTotal) * 100 : null,
    netMarginPercent: priceTotal > 0 ? (netMarginAmount / priceTotal) * 100 : null,
    netMarginAmount,
  };
}
